import React, { useState, useEffect, useCallback } from "react";
import LocationFilterToolbar from "../../App/LocationFilterToolbar";
import AppBar from "./../../components/DefaultAppBar";
import { makeStyles } from "@mui/styles";
import ProfileHeader from "./ProfileHeader";
import FolderList from "../../components/Common/FolderList";
import IOSwitch from "../../components/Common/IOSCheckbox";
import { updateAccount, getCurrentAccount } from "../../modules/accounts";
import { ListItem, FormControl, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { currentEstateSelectors } from "@/modules/estate/currentEstate/currentEstateSlice";
import { useAppSelector } from "@/store/store";
import DocumentLayout from "../../components/Layouts/DocumentLayout";

export const route = "/profile/privacy";

const notificationSettingsContent = [
  {
    name: {
      id: "settings.public.tickets.new",
      defaultMessage: "Eine neue öffentliche Mitteilung",
    },
    type: "onNewPublicTicket",
  },
  {
    name: {
      id: "settings.public.tickets.answer",
      defaultMessage: "Eine Antwort auf eine öffentliche Mitteilung",
    },
    type: "onPublicTicketAnswer",
  },
  {
    name: {
      id: "settings.public.tickets.statusChange",
      defaultMessage: "Ein neuer Status einer Mitteilung",
    },
    type: "onPublicTicketStatusChange",
  },
  {
    name: {
      id: "settings.documents.new",
      defaultMessage: "Ein neues Dokument",
    },
    type: "onNewDocument",
  },
];

const useSettingsStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: theme.spacing(),
    paddingBottom: 0,
  },
}));

const NotificationSettingsList = React.memo(function NotificationSettingsList({
  data,
  defaultSettings,
  onChange,
}) {
  const classes = useSettingsStyles();
  const { formatMessage } = useIntl();
  const [values, setValues] = useState(() => ({ ...(defaultSettings || {}) }));

  useEffect(() => {
    setValues({ ...(defaultSettings || {}) });
  }, [defaultSettings]);

  const handleToggle = useCallback(
    (field, value) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      onChange(field, value);
    },
    [onChange],
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <FolderList data={data}>
        {(document, index) => (
          <ListItem
            key={`${index}-${document.id}`}
            className={classes.container}
          >
            <Typography variant="subtitle1" inline>
              {formatMessage(document.name)}
            </Typography>
            <FormControl style={{ marginBottom: "12px" }}>
              <IOSwitch
                name={document.type}
                value={!!values[document.type]}
                checked={!!values[document.type]}
                onChange={(event, value) => handleToggle(document.type, value)}
              />
            </FormControl>
          </ListItem>
        )}
      </FolderList>
    </form>
  );
});

/**
 *
 */
const useStyles = makeStyles((theme) => ({
  center: {
    display: "flex",
    margin: "50% auto",
    fontSize: "10rem",
  },
  button: {
    width: "100%",
  },

  content: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    color: "rgba(0, 0, 0, 0.54)",
  },
}));

const ProfileContainer = () => {
  const [notificationSettings, setNotificationSettings] = useState({});
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const currentEstate = useAppSelector((state) =>
    currentEstateSelectors.selectCurrentEstate(state),
  );

  useEffect(() => {
    const accountId = currentAccount?.accountId;
    if (!accountId) {
      return;
    }

    let isMounted = true;

    getCurrentAccount(accountId).then(({ data }) => {
      if (!isMounted) {
        return;
      }
      setNotificationSettings((data || {}).notificationSettings || {});
    });

    return () => {
      isMounted = false;
    };
  }, [currentAccount?.accountId]);

  const handleChange = useCallback(
    (field, value) => {
      const accountId = currentAccount?.accountId;
      if (!accountId) {
        return;
      }

      setNotificationSettings((prev) => {
        const newSettings = { ...prev, [field]: value };
        updateAccount(accountId, newSettings).then((data) => {
          if (data) {
            setNotificationSettings(data);
          }
        });
        return newSettings;
      });
    },
    [currentAccount?.accountId],
  );

  return (
    <div>
      <AppBar position="static">
        <LocationFilterToolbar noTicketFilter />
      </AppBar>

      <ProfileHeader estate={currentEstate} />

      <DocumentLayout>
        <Typography variant="h6" className={classes.content}>
          {formatMessage({ id: "profile.privacy.title.short" })}
        </Typography>

        <Typography className={classes.content}>
          {formatMessage({ id: "profile.privacy.title" })}
        </Typography>

        <Typography className={classes.content}>
          {formatMessage({ id: "profile.privacy.settings.desc" })}
        </Typography>

        {/* <div dangerouslySetInnerHTML={{ __html: title }} /> */}

        <NotificationSettingsList
          data={notificationSettingsContent}
          onChange={handleChange}
          defaultSettings={notificationSettings}
        />
      </DocumentLayout>
    </div>
  );
};

ProfileContainer.route = route;

export default ProfileContainer;
